// src/scenes/contracts/ContractFormPage/RevendaTab.jsx
import {
  Box,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from '../../../hooks/useTranslation';
import { greenColor } from '../../../config/colors';
import { formatCpfCnpj, formatCep, formatNumericInput } from '../../../utils';
import CustomTable from '../../../components/CustomTable';

const TAX_REGIMES = [
  { value: 'SIMPLES', label: 'Simples Nacional' },
  { value: 'PRESUMIDO', label: 'Lucro Presumido' },
  { value: 'REAL', label: 'Lucro Real' },
  { value: 'MEI', label: 'MEI' },
];

const BR_STATES = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA',
  'MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN',
  'RS','RO','RR','SC','SP','SE','TO',
];

const fieldLabelSx = {
  fontSize: '11px',
  fontWeight: 600,
  color: '#9e9e9e',
  mb: 0.5,
  letterSpacing: '0.5px',
};

const sectionTitleSx = {
  fontWeight: 700,
  fontSize: '13px',
  mb: 2,
  letterSpacing: '0.5px',
  color: '#333',
};

const inputSx = {
  '& .MuiInputBase-root': { fontSize: '14px', backgroundColor: '#fff' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
};

const selectSx = {
  backgroundColor: '#fff',
  fontSize: '14px',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
};

const LabeledField = ({ label, children }) => (
  <Box>
    <Typography sx={fieldLabelSx}>{label}</Typography>
    {children}
  </Box>
);

const RevendaTab = ({ formData, onChange }) => {
  const { t } = useTranslation();
  const s = 'contractForm.revenda';

  const handle = (field) => (e) => onChange({ [field]: e.target.value });

  const handleFormatted = (field, type) => (e) => {
    onChange({ [field]: formatNumericInput(e.target.value, { type }) });
  };

  const handleCnpj = (field) => (e) => {
    onChange({ [field]: formatCpfCnpj(e.target.value) });
  };

  const handleCep = (field) => (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
    onChange({ [field]: formatCep(digits) });
  };

  const revendaSocios = formData.revendaSocios ?? [
    { id: Date.now(), nome: '', email: '', cpf: '', rg: '' },
  ];

  // Update revendaSócio field by ID
  const handleSocioById = (id, field, value) => {
    const updated = revendaSocios.map(s => s.id === id ? { ...s, [field]: value } : s);
    onChange({ revendaSocios: updated });
  };

  const handleSocioCpfById = (id, value) => {
    handleSocioById(id, 'cpf', formatCpfCnpj(value));
  };

  const handleAddSocio = () => {
    onChange({
      revendaSocios: [...revendaSocios, { id: Date.now(), nome: '', email: '', cpf: '', rg: '' }],
    });
  };

  const handleRemoveSocioById = (id) => {
    const updated = revendaSocios.filter(s => s.id !== id);
    onChange({
      revendaSocios: updated.length
        ? updated
        : [{ id: Date.now(), nome: '', email: '', cpf: '', rg: '' }],
    });
  };

  // Build CustomTable columns — defined inside component to capture handlers via closure
  const sociosColumns = [
    {
      field: 'nome',
      headerName: t(`${s}.partnerName`),
      render: (value, row) => (
        <TextField
          value={value || ''}
          onChange={(e) => handleSocioById(row.id, 'nome', e.target.value)}
          size="small"
          variant="standard"
          fullWidth
          placeholder="Nome completo"
          inputProps={{ style: { fontSize: '14px' } }}
        />
      ),
    },
    {
      field: 'email',
      headerName: t(`${s}.partnerEmail`),
      render: (value, row) => (
        <TextField
          value={value || ''}
          onChange={(e) => handleSocioById(row.id, 'email', e.target.value)}
          size="small"
          variant="standard"
          fullWidth
          placeholder="email@empresa.com"
          inputProps={{ style: { fontSize: '14px' } }}
        />
      ),
    },
    {
      field: 'cpf',
      headerName: t(`${s}.partnerCpf`),
      render: (value, row) => (
        <TextField
          value={value || ''}
          onChange={(e) => handleSocioCpfById(row.id, e.target.value)}
          size="small"
          variant="standard"
          fullWidth
          placeholder="000.000.000-00"
          inputProps={{ style: { fontSize: '14px' } }}
        />
      ),
    },
    {
      field: 'rg',
      headerName: t(`${s}.partnerRg`),
      render: (value, row) => (
        <TextField
          value={value || ''}
          onChange={(e) => handleSocioById(row.id, 'rg', e.target.value)}
          size="small"
          variant="standard"
          fullWidth
          placeholder="0000000"
          inputProps={{ style: { fontSize: '14px' } }}
        />
      ),
    },
  ];

  // Only show delete action when there is more than one sócio
  const sociosActions = revendaSocios.length > 1
    ? [{
        icon: <DeleteOutlineIcon fontSize="small" sx={{ color: '#ef5350' }} />,
        tooltip: 'Remover sócio',
        onClick: (row) => handleRemoveSocioById(row.id),
      }]
    : [];

  return (
    <Box>
      {/* REPASSE */}
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5, mb: 3 }}>
        <Typography sx={sectionTitleSx}>{t(`${s}.repasse`)}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <LabeledField label={t(`${s}.repassePct`)}>
              <TextField
                value={formData.repassePorcentagem}
                onChange={handleFormatted('repassePorcentagem', 'percent')}
                size="small"
                fullWidth
                placeholder="0,00"
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={6}>
            <LabeledField label={t(`${s}.repasseValue`)}>
              <TextField
                value={formData.repasseValor}
                onChange={handleFormatted('repasseValor', 'currency')}
                size="small"
                fullWidth
                placeholder="0,00"
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
        </Grid>
      </Box>

      {/* DETALHES DA REVENDA */}
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5, mb: 3 }}>
        <Typography sx={sectionTitleSx}>{t(`${s}.details`)}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <LabeledField label={t(`${s}.nameRazao`)}>
              <TextField
                value={formData.revendaNome}
                onChange={handle('revendaNome')}
                size="small"
                fullWidth
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.cnpj`)}>
              <TextField
                value={formData.revendaCnpj}
                onChange={handleCnpj('revendaCnpj')}
                size="small"
                fullWidth
                placeholder="00.000.000/0000-00"
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.municipalReg`)}>
              <TextField
                value={formData.revendaInscMunicipal}
                onChange={handle('revendaInscMunicipal')}
                size="small"
                fullWidth
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.stateReg`)}>
              <TextField
                value={formData.revendaInscEstadual}
                onChange={handle('revendaInscEstadual')}
                size="small"
                fullWidth
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.taxRegime`)}>
              <FormControl fullWidth size="small">
                <Select
                  value={formData.revendaRegimeTributario}
                  onChange={handle('revendaRegimeTributario')}
                  displayEmpty
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em style={{ fontStyle: 'normal', color: '#9e9e9e' }}>Selecione</em>
                  </MenuItem>
                  {TAX_REGIMES.map((r) => (
                    <MenuItem key={r.value} value={r.value} sx={{ fontSize: '14px' }}>
                      {r.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </LabeledField>
          </Grid>
        </Grid>
      </Box>

      {/* ENDEREÇO */}
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5, mb: 3 }}>
        <Typography sx={sectionTitleSx}>{t(`${s}.address`)}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <LabeledField label={t(`${s}.street`)}>
              <TextField
                value={formData.revendaLogradouro}
                onChange={handle('revendaLogradouro')}
                size="small"
                fullWidth
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.number`)}>
              <TextField
                value={formData.revendaNumero}
                onChange={handle('revendaNumero')}
                size="small"
                fullWidth
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.neighborhood`)}>
              <TextField
                value={formData.revendaBairro}
                onChange={handle('revendaBairro')}
                size="small"
                fullWidth
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.complement`)}>
              <TextField
                value={formData.revendaComplemento}
                onChange={handle('revendaComplemento')}
                size="small"
                fullWidth
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.cep`)}>
              <TextField
                value={formData.revendaCep}
                onChange={handleCep('revendaCep')}
                size="small"
                fullWidth
                placeholder="00000-000"
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.state`)}>
              <FormControl fullWidth size="small">
                <Select
                  value={formData.revendaEstado}
                  onChange={handle('revendaEstado')}
                  displayEmpty
                  sx={selectSx}
                >
                  <MenuItem value="">
                    <em style={{ fontStyle: 'normal', color: '#9e9e9e' }}>Selecione</em>
                  </MenuItem>
                  {BR_STATES.map((uf) => (
                    <MenuItem key={uf} value={uf} sx={{ fontSize: '14px' }}>{uf}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={4}>
            <LabeledField label={t(`${s}.city`)}>
              <TextField
                value={formData.revendaCidade}
                onChange={handle('revendaCidade')}
                size="small"
                fullWidth
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
        </Grid>
      </Box>

      {/* CONTATOS */}
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5, mb: 3 }}>
        <Typography sx={sectionTitleSx}>{t(`${s}.contacts`)}</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <LabeledField label={t(`${s}.financialResponsible`)}>
              <TextField
                value={formData.revendaNomeFinanceiro}
                onChange={handle('revendaNomeFinanceiro')}
                size="small"
                fullWidth
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={6}>
            <LabeledField label={t(`${s}.financialEmail`)}>
              <TextField
                value={formData.revendaEmailFinanceiro}
                onChange={handle('revendaEmailFinanceiro')}
                size="small"
                fullWidth
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
          <Grid item xs={12} md={6}>
            <LabeledField label={t(`${s}.creditEmail`)}>
              <TextField
                value={formData.revendaEmailCredito}
                onChange={handle('revendaEmailCredito')}
                size="small"
                fullWidth
                sx={inputSx}
              />
            </LabeledField>
          </Grid>
        </Grid>
      </Box>

      {/* SÓCIOS DA REVENDA — usando CustomTable com col.render() para campos editáveis */}
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2.5 }}>
        <Typography sx={sectionTitleSx}>{t(`${s}.partners`)}</Typography>
        <CustomTable
          columns={sociosColumns}
          rows={revendaSocios}
          rowKey="id"
          actions={sociosActions}
          hidePagination
          elevation={0}
          containerSx={{ borderRadius: 1 }}
          emptyMessage="Nenhum sócio adicionado."
        />
        <Typography
          onClick={handleAddSocio}
          sx={{
            fontSize: '13px',
            color: greenColor,
            fontWeight: 600,
            cursor: 'pointer',
            mt: 2,
            display: 'inline-flex',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {t(`${s}.addPartner`)}
        </Typography>
      </Box>
    </Box>
  );
};

export default RevendaTab;
